// Shared utilities for image paste/upload flows
// Centralizes client-side validation and placeholder generation
import type React from 'react';

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB (align with backend default)

export type ValidateOptions = {
  acceptImagesOnly?: boolean;
  maxSizeBytes?: number;
};

export type ValidateResult = {
  valid: File[];
  errors: string[];
};

export function filesFromClipboardEvent(e: ClipboardEvent | React.ClipboardEvent<any>): File[] {
  const dtItems = (e as any).clipboardData?.items as DataTransferItemList | undefined;
  const imageItems = dtItems ? Array.from(dtItems).filter((item) => item.type?.startsWith('image/')) : [];
  const files: File[] = [];
  imageItems.forEach((item, idx) => {
    const blob = item.getAsFile();
    if (!blob) return;
    const ext = (blob.type?.split('/')[1] || 'png').toLowerCase();
    const filename = `clipboard-${Date.now()}-${idx}.${ext}`;
    files.push(new File([blob], filename, { type: blob.type || 'image/png' }));
  });
  return files;
}

export function validateFiles(files: File[], options: ValidateOptions = {}): ValidateResult {
  const { acceptImagesOnly = false, maxSizeBytes = MAX_FILE_SIZE_BYTES } = options;
  const valid: File[] = [];
  const errors: string[] = [];

  files.forEach((file) => {
    const type = file.type || '';
    const sizeOk = typeof file.size === 'number' ? file.size <= maxSizeBytes : true;
    const typeOk = acceptImagesOnly ? type.startsWith('image/') : true;

    if (!sizeOk) {
      errors.push(`${file.name}: arquivo excede ${Math.round(maxSizeBytes / (1024 * 1024))}MB`);
      return;
    }
    if (!typeOk) {
      errors.push(`${file.name}: tipo não suportado (somente imagens)`);
      return;
    }
    valid.push(file);
  });

  return { valid, errors };
}

export type PendingImage = { id: string; file: File; alt: string };

function generateSecureId(prefix = 'img'): string {
  const c = (globalThis as any).crypto as Crypto | undefined;
  if (c && typeof (c as any).randomUUID === 'function') {
    return `${prefix}-${(c as any).randomUUID()}`;
  }
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${prefix}-${hex}`;
  }
  return `${prefix}-${Date.now().toString(36)}`;
}

export function placeholdersForPendingImages(files: File[]): { pending: PendingImage[]; markdown: string } {
  const pending: PendingImage[] = [];
  const mdLines: string[] = [];
  files.forEach((file) => {
    const id = generateSecureId('img');
    const alt = file.name.split('.').slice(0, -1).join('.') || 'imagem';
    pending.push({ id, file, alt });
    mdLines.push(`![${alt}](pending:${id})`);
  });
  return { pending, markdown: mdLines.join('\n') };
}
